import React, { useState, useEffect } from 'react';
import { ucFirst, hasHst, getDeviceLabel } from '../../common/ambient';
import classNames from 'classnames';
import Avatar from '../user/Avatar';
import { alertSetOk } from '.'
import bindAllActions from '../../common/bindAllActions'
import { path } from 'ramda'
import { UtilityMenu } from '../../components'
import {
  isValidPhone,
  isEmailVerified,
  isPhoneVerified,
  hasVerifiedContact as hasVerifiedContactHelper,
  getEmailInviteStatus,
  getPhoneInviteStatus,
  createInviteAction,
  shouldShowEmailField,
  shouldShowPhoneField,
  hasValidContact as hasValidContactHelper,
  validateForm as validateFormHelper
} from './AlertSetMemberCard.helpers'
import { formatPhoneInput } from './phoneVerification.helpers'

const AlertSetMemberCard = ({ member, alertSets, devices, showDevices, onEdit, onRemove, socialActions, user, userActionTypePrefix, showAlertSets, showUtilityMenu, ...props }) => {
  alertSets = alertSets || []
  const _devices = devices || []
  const _userActionTypePrefix = userActionTypePrefix || 'alert-set-group-member'
  const _showAlertSets = showAlertSets !== false
  const _showDevices = showDevices === true
  const _showUtilityMenu = showUtilityMenu !== undefined ? showUtilityMenu : true

  const [sendingInvite, setSendingInvite] = useState(false);
  const [sendingEmailInvite, setSendingEmailInvite] = useState(false);

  // Helper function to send email verification
  const sendEmailVerification = (memberData, onSuccess, onError) => {
    setSendingEmailInvite(true);

    const action = createInviteAction(
      _userActionTypePrefix,
      'email',
      member._id,
      memberData.email || member.email,
      memberData.name || member.name
    );

    socialActions.addUserAction(action)
    .then(res => {
      setSendingEmailInvite(false);
      if (res && res._id && onSuccess) {
        onSuccess(res);
      }
    })
    .catch(err => {
      setSendingEmailInvite(false);
      console.error('Failed to create user action:', err);
      if (onError) {
        onError(err);
      }
    });
  };

  // Icon mapping for field labels
  const fieldIcons = {
    email: 'glyphicon glyphicon-envelope',
    phone: 'glyphicon glyphicon-comment'
  };

  // Render all contact status tags grouped together
  const renderContactStatusTags = () => {
    const tags = [];

    // Email tag
    if (member.email) {
      const emailStatus = getEmailInviteStatus(member, user);
      if (emailStatus === 'verified' || emailStatus === 'pending') {
        tags.push({
          key: 'email',
          icon: fieldIcons.email,
          status: emailStatus,
          text: emailStatus === 'verified' ? 'active' : 'pending'
        });
      }
    }

    // Phone tag
    if (member.phone) {
      const phoneStatus = getPhoneInviteStatus(member, user);
      if (phoneStatus === 'verified' || phoneStatus === 'pending') {
        tags.push({
          key: 'phone',
          icon: fieldIcons.phone,
          status: phoneStatus,
          text: phoneStatus === 'verified' ? 'active' : 'pending'
        });
      }
    }

    if (tags.length === 0) {
      return <div className="contact-status-tags-container" />
    }

    return (
      <div className="contact-status-tags-container">
        {tags.map(tag => (
          <div key={tag.key} className={`contact-status-tag ${tag.status === 'verified' ? 'active' : 'pending'}`}>
            <i className={tag.icon} />
            <span>{tag.text}</span>
          </div>
        ))}
      </div>
    );
  };

  const renderField = (label, value, fieldName) => {
    // If value is not set, don't render the field at all
    if (!value) {
      return null;
    }

    // Format phone numbers for display
    const displayValue = fieldName === 'phone' ? formatPhoneInput(value) : value;

    return (
      <div className="form-line">
        <span className="value">{displayValue}</span>
      </div>
    );
  };
  const renderAlertSets = () => {
    // If no alert sets, don't show this section
    if (!member.alertSets || member.alertSets.length === 0) {
      return null
    }

    const alertOptions = []
    if (isEmailVerified(member, user)) {
      alertOptions.push('email')
    }
    if (isPhoneVerified(member, user)) {
      alertOptions.push('sms')
    }

    const prefClasses = {
      email: 'glyphicon glyphicon-envelope',
      sms: 'glyphicon glyphicon-comment',
    }

    return (
      <div className="form-line render-alert-sets">
        <label>Alert Sets</label>
        <div>
          <div className="member-alert-sets">
            {member.alertSets.map((as, i) => {
              const alertSet = alertSets.find(_as => _as._id === as._id)
              const title = alertSet ? alertSet.title : 'alert set removed'
              return (
                <div className={classNames('member-alert-set alert-description', { inactive: !alertSet })} key={i}>
                  <span>{title}</span>
                  <div className='tools'>
                    {alertOptions.map(pref => {
                      const isActive = as.alertPrefs.includes(pref)
                      return (
                        <i
                          key={pref}
                          className={classNames(prefClasses[pref], { active: isActive })}
                        />
                      )
                    })}
                  </div>
                </div>
              )
            })}
          </div>
        </div>
      </div>
    )
  }

  const renderDevices = () => {
    // Combine both device arrays
    const allDeviceMacs = [
      ...(member.devicesEmail || []),
      ...(member.devicesSms || [])
    ]

    if (allDeviceMacs.length === 0) {
      return null
    }

    const uniqueMacs = [...new Set(allDeviceMacs)]

    const alertOptions = []
    if (isEmailVerified(member, user)) {
      alertOptions.push('email')
    }
    if (isPhoneVerified(member, user)) {
      alertOptions.push('sms')
    }

    const prefClasses = {
      email: 'glyphicon glyphicon-envelope',
      sms: 'glyphicon glyphicon-comment',
    }

    return (
      <div className="form-line render-alert-sets">
        <label>Heat Stress Devices</label>
        <div>
          <div className="member-alert-sets">
            {uniqueMacs.map((mac, i) => {
              const device = _devices.find(d => d.macAddress === mac)
              const title = device ? getDeviceLabel(device) : 'device removed'

              return (
                <div className={classNames('member-alert-set alert-description', { inactive: !device })} key={i}>
                  <span>{title}</span>
                  <div className='tools'>
                    {alertOptions.map(pref => {
                      const deviceArray = pref === 'email' ? member.devicesEmail : member.devicesSms
                      const isActive = deviceArray && deviceArray.includes(mac)
                      return (
                        <i
                          key={pref}
                          className={classNames(prefClasses[pref], { active: isActive })}
                        />
                      )
                    })}
                  </div>
                </div>
              )
            })}
          </div>
        </div>
      </div>
    )
  }

  const handleCardClick = () => {
    if (onEdit) {
      onEdit(member)
    }
  }

  return (
    <div
      className={classNames("group-member-card", "not-editing", { clickable: onEdit })}
      onClick={handleCardClick}
    >
      <header>
        <Avatar name={!member.admin && (member.name || '?')} />
        {member.admin ? (
          <span className="member-name">Group Admin (You)</span>
        ) : (
          <span className="member-name">{member.name || 'Unnamed Member'}</span>
        )}
        {_showUtilityMenu && !member.admin && (
          <UtilityMenu>
            <a
              key='edit'
              onClick={(e) => {
                e.stopPropagation()
                if (onEdit) onEdit(member)
              }}
            >
              Edit
            </a>
            {onRemove && (
              <a
                key='remove'
                onClick={(e) => {
                  e.stopPropagation()
                  onRemove()
                }}
              >
                Remove
              </a>
            )}
          </UtilityMenu>
        )}
      </header>

      <>
        {renderContactStatusTags()}
        {renderField('Email', member.email, 'email')}
        {renderField('Phone', member.phone, 'phone')}
      </>

      {_showAlertSets && renderAlertSets()}

      {_showAlertSets && hasVerifiedContactHelper(member, user) && (!member.alertSets || member.alertSets.length === 0) && (
        <div className="assign-alert-sets-container">
          <a
            className="btn btn-primary assign-alert-sets-btn"
            onClick={(e) => {
              e.stopPropagation();
              if (onEdit) {
                onEdit(member);
              }
            }}
          >
            Assign Alert Sets
          </a>
        </div>
      )}

      {_showDevices && renderDevices()}

      {_showDevices && hasVerifiedContactHelper(member, user) &&
        (!member.devicesEmail || member.devicesEmail.length === 0) &&
        (!member.devicesSms || member.devicesSms.length === 0) && (
        <div className="assign-alert-sets-container">
          <a
            className="btn btn-primary assign-alert-sets-btn"
            onClick={(e) => {
              e.stopPropagation();
              if (onEdit) {
                onEdit(member);
              }
            }}
          >
            Assign Devices
          </a>
        </div>
      )}
    </div>
  );
};

export default bindAllActions(AlertSetMemberCard);

AlertSetMemberCard.propTypes = {};
AlertSetMemberCard.defaultProps = {
  userActionTypePrefix: 'alert-set-group-member',
  showAlertSets: true,
  showUtilityMenu: true,
  showDevices: false,
  devices: []
};
AlertSetMemberCard.displayName = 'AlertSetMemberCard'
